import {isAxiosError} from "axios";

/**
 * The waits between two attempts, about a minute and a half in total: long
 * enough to outlast a counter PC whose network comes up well after Windows
 * has started Connect.
 */
const DEFAULT_DELAYS = [2000, 4000, 8000, 15000, 15000, 30000, 30000];

/**
 * Whether the error means the API could not be reached, rather than that it
 * refused the request: no response at all (network down, DNS not ready,
 * timeout) or a server-side failure. A 4xx is an answer, never retried.
 */
export const isUnreachable = (error: unknown): boolean => {
    if (!isAxiosError(error)) {
        return false;
    }

    return !error.response || error.response.status >= 500;
};

/**
 * Run the request, running it again while the API is unreachable.
 *
 * Meant for the startup requests: a counter PC boots, Connect starts before
 * the network is up, and a single failed call used to send the pharmacy back
 * to the login screen — with a still valid token — every morning.
 *
 * @param request The request to run.
 * @param onRetry Called before each new attempt, e.g. to tell the user what is going on.
 * @param delays The waits between two attempts, their count bounding the retries.
 */
export default async function retryWhileUnreachable<T>(
    request: () => Promise<T>,
    onRetry?: () => void,
    delays: number[] = DEFAULT_DELAYS
): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await request();
        } catch (error) {
            if (attempt >= delays.length || !isUnreachable(error)) {
                throw error;
            }

            onRetry?.();

            await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        }
    }
}
